/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2020
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { useTheme } from "@material-ui/core/styles";
import Backdrop from "@material-ui/core/Backdrop";
import CloseIcon from "@material-ui/icons/Close";
import Grid from "@material-ui/core/Grid";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";

import { fetchCarrier, resetCarrier } from "../slices/carrierSlice";
import { fetchLegs, resetLegs } from "../slices/legsSlice";
import { fetchNotes, markNotesRead, resetNotes } from "../slices/notesSlice";
import { updateRoute } from "../slices/coveredRoutesSlice";
import types from "../helpers/types";
import CarrierDetails from "../components/route-details/CarrierDetails";
import PreviousLegsDetails from "../components/route-details/PreviousLegsDetails";
import ScheduledLegsDetails from "../components/route-details/ScheduledLegsDetails";
import TrackingUpdateForm from "../components/route-details/TrackingUpdateForm";
import Loader from "../components/Loader";
import WatchedIcon from "../components/common/WatchedIcon";
import Notification from "../components/common/Notification";
import NotesPanel from "../components/common/NotesPanel";
import UnsavedChangesModal from "../components/common/UnsavedChangesModal";
import { postWatch, deleteWatch } from "../api/api";

const StyledHeader = styled(({ theme, ...props }) => <Grid {...props} />)`
  background-color: ${(props) => props.theme.palette.background.lightGrey};
  padding: 12px 16px;
`;

const StyledGridContainer = styled(Grid)`
  padding: 16px;
`;

const StyledGridHeaderItem = styled(Grid)`
  flex: 0 0 100%;
`;

function RouteDetails({
  openedRoute,
  setOpenedRoute,
  setUnsavedModalOpen,
  unsavedModalOpen,
  setUnsavedChanges,
  unsavedChanges,
}) {
  // hooks
  const theme = useTheme();
  const dispatch = useDispatch();

  // global state
  const { carrier, requestStatus: carrierRequestStatus } = useSelector(
    (state) => state.carrier
  );
  const { legs, requestStatus: legsRequestStatus } = useSelector(
    (state) => state.legs
  );
  const {
    notes,
    getRequestStatus: getNotesRequestStatus,
    patchRequestStatus: patchNoteRequestStatus,
  } = useSelector((state) => state.notes);

  // local state
  const [routeId, setRouteId] = useState(openedRoute.id);
  const [watched, setWatched] = useState(openedRoute.watched);
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // feature flags

  // derived data
  const previousLegs =
    legs?.filter((leg) => leg.legStatus === types.legStatuses.complete) ?? [];
  const scheduledLegs =
    legs?.filter(
      (leg) =>
        leg.legStatus === types.legStatuses.active ||
        leg.legStatus === types.legStatuses.ready
    ) ?? [];
  const currentLeg = legs?.find(
    (leg) => leg.legSequence === openedRoute.currentLeg
  );

  const loading =
    carrierRequestStatus === types.requestStatuses.inProgress ||
    legsRequestStatus === types.requestStatuses.inProgress ||
    getNotesRequestStatus === types.requestStatuses.inProgress ||
    patchNoteRequestStatus === types.requestStatuses.inProgress;

  // effects
  useEffect(() => {
    if (
      openedRoute.carrierId > 0 &&
      carrierRequestStatus === types.requestStatuses.initial
    ) {
      dispatch(fetchCarrier(openedRoute.carrierId));
    }
  }, [dispatch, openedRoute, carrierRequestStatus]);

  useEffect(() => {
    if (
      openedRoute.id > 0 &&
      legsRequestStatus === types.requestStatuses.initial
    ) {
      dispatch(fetchLegs(openedRoute.id));
    }
  }, [dispatch, openedRoute, legsRequestStatus]);

  useEffect(() => {
    if (openedRoute.id !== routeId) {
      setRouteId(openedRoute.id);
      setWatched(openedRoute.watched);
    }
  }, [dispatch, openedRoute, routeId]);

  useEffect(() => {
    if (
      currentLeg?.id > 0 &&
      getNotesRequestStatus === types.requestStatuses.initial
    ) {
      dispatch(fetchNotes(currentLeg.id));
    }
  }, [dispatch, currentLeg, getNotesRequestStatus]);

  useEffect(() => {
    const unreadNotes = notes?.filter(
      (note) => !note.isRead && note.position === "left"
    );

    if (
      currentLeg &&
      unreadNotes?.length > 0 &&
      patchNoteRequestStatus === types.requestStatuses.initial
    ) {
      dispatch(markNotesRead(currentLeg.id, unreadNotes));
    }
  }, [dispatch, currentLeg, notes, patchNoteRequestStatus]);

  useEffect(() => {
    if (openedRoute.noteReadStatus === types.noteReadStatus.unread) {
      dispatch(
        updateRoute({
          currentLeg: openedRoute.currentLeg,
          routeId: openedRoute.id,
          updates: {
            noteReadStatus: types.noteReadStatus.read,
          },
        })
      );
    }
    else if (openedRoute.notes) {
      dispatch(
        updateRoute({
          currentLeg: openedRoute.currentLeg,
          routeId: openedRoute.id,
          updates: {
            notes: false,
          },
        })
      );
    }
  }, []);

  // handlers
  const handleCloseButtonClick = () => {
    if (unsavedChanges) {
      setUnsavedModalOpen(true);
    } else {
      setOpenedRoute(null);
      dispatch(resetCarrier());
      dispatch(resetLegs());
      dispatch(resetNotes());
    }
  };

  const handleModalAffirmative = () => {
    setUnsavedModalOpen(false);
  };

  const handleModalNegative = () => {
    setUnsavedModalOpen(false);
    setUnsavedChanges(false);
    setOpenedRoute(null);
  };

  const handleWatchButtonClick = () => {
    dispatch(
      updateRoute({
        currentLeg: openedRoute.currentLeg,
        routeId: openedRoute.id,
        updates: {
          watched: !watched,
        },
      })
    );

    if (watched) {
      deleteWatch(currentLeg.id);
      setWatched(false);
    } else {
      postWatch(currentLeg.id);
      setWatched(true);
    }
  };

  return (
    <div style={{ minHeight: "100%" }} data-testid="covered-route-details">
      <StyledHeader container alignItems="center" theme={theme}>
        <Grid item>
          <Typography variant="body1" component="h2">
            <span style={{ fontWeight: "bold" }}>Route Code:</span>{" "}
            {openedRoute.code}-{openedRoute.currentLeg}
          </Typography>
        </Grid>
        <Grid item style={{ paddingLeft: "10px" }}>
          <Typography variant="body1" component="h2">
            <span style={{ fontWeight: "bold" }}>Route ID:</span>{" "}
            {openedRoute.upsRouteId}
          </Typography>
        </Grid>
        <Grid item style={{ marginLeft: "auto" }}>
          {(currentLeg?.legStatus === types.legStatuses.active ||
            currentLeg?.legStatus === types.legStatuses.ready) && (
              <IconButton
                size="small"
                onClick={handleWatchButtonClick}
                style={{ marginRight: "4px" }}
              >
                <WatchedIcon
                  color={watched ? null : theme.palette.grey[600]}
                  data-testid="covered-route-details-watch-button"
                />
              </IconButton>
            )}
          <IconButton size="small" onClick={handleCloseButtonClick}>
            <CloseIcon
              fontSize="small"
              data-testid="covered-route-details-close-button"
            />
          </IconButton>
        </Grid>
      </StyledHeader>
      <div style={{ contain: "paint", minHeight: "100%" }}>
        <Backdrop
          open={loading}
          style={{
            zIndex: theme.zIndex.drawer + 1,
            backgroundColor: "#fff",
            opacity: 1,
          }}
        >
          <Loader />
        </Backdrop>
        {(currentLeg?.legStatus === types.legStatuses.active ||
          currentLeg?.legStatus === types.legStatuses.ready) &&
          openedRoute && (
            <StyledGridContainer
              item
              container
              wrap="wrap"
              data-testid="tracking-update"
            >
              <StyledGridHeaderItem item component="h3">
                Tracking Update
              </StyledGridHeaderItem>
              <TrackingUpdateForm
                previousLeg={legs.find(
                  (leg) => leg.legSequence === currentLeg.legSequence - 1
                )}
                currentLeg={currentLeg}
                openedRoute={openedRoute}
                setShowSuccessNotification={setShowSuccessNotification}
                setUnsavedChanges={setUnsavedChanges}
              />
            </StyledGridContainer>
          )}
        {carrier && (
          <StyledGridContainer
            item
            container
            wrap="wrap"
            data-testid="carrier-details"
          >
            <StyledGridHeaderItem item component="h3">
              Carrier
            </StyledGridHeaderItem>
            <CarrierDetails carrier={carrier} />
          </StyledGridContainer>
        )}
        {previousLegs.length > 0 && (
          <StyledGridContainer item container>
            <StyledGridHeaderItem item component="h3">
              Previous Legs
            </StyledGridHeaderItem>
            <PreviousLegsDetails legs={previousLegs} />
          </StyledGridContainer>
        )}
        {scheduledLegs.length > 0 && (
          <StyledGridContainer item container>
            <StyledGridHeaderItem item component="h3">
              Scheduled Legs
            </StyledGridHeaderItem>
            <ScheduledLegsDetails legs={scheduledLegs} />
          </StyledGridContainer>
        )}
        {getNotesRequestStatus === types.requestStatuses.success && (
          <StyledGridContainer item container>
            <StyledGridHeaderItem item component="h3">
              Notes
            </StyledGridHeaderItem>
            <NotesPanel notes={notes} />
          </StyledGridContainer>
        )}
      </div>
      <Notification
        message="Route updated."
        open={showSuccessNotification}
        setOpen={setShowSuccessNotification}
      />
      <UnsavedChangesModal
        open={unsavedModalOpen}
        handleAffirmative={handleModalAffirmative}
        handleNegative={handleModalNegative}
        message="This leg has unsaved changes. Leaving will discard those changes."
      />
    </div>
  );
}

RouteDetails.propTypes = {
  openedRoute: PropTypes.object,
  setOpenedRoute: PropTypes.func.isRequired,
  setUnsavedModalOpen: PropTypes.func.isRequired,
  setUnsavedChanges: PropTypes.func.isRequired,
  unsavedChanges: PropTypes.bool.isRequired,
  unsavedModalOpen: PropTypes.bool.isRequired,
};

export default RouteDetails;
